import { useEffect, useMemo } from 'react';

// Feu d'artifice + felicitations quand un ouvrier coche une reserve (Pavel 31/10/2026).
// `final` = c'etait la derniere reserve ouverte du chantier : grande fete.
const styles = `
@keyframes rc-burst { 0% { transform: translate(0,0) scale(1); opacity: 1; } 100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; } }
@keyframes rc-fall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(900deg); opacity: 0.2; } }
@keyframes rc-pop { 0% { transform: scale(0.4); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes rc-shine { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.15); } }
`;

const COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF8C00', '#14b8a6'];

export default function ReceptionCelebration({ language, name, number, final, onClose }) {
  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);

  useEffect(() => {
    // Petite vibration sur le telephone, comme un vrai petard
    try { navigator.vibrate?.(final ? [120, 60, 120, 60, 240] : [70, 40, 140]); } catch (e) { /* non supporte */ }
    const t = setTimeout(onClose, final ? 6000 : 4200);
    return () => clearTimeout(t);
  }, [onClose, final]);

  const bursts = useMemo(() => {
    const centers = final ? [[30, 30], [70, 25], [50, 55], [20, 65], [80, 70]] : [[25, 30], [75, 28], [50, 62]];
    const out = [];
    centers.forEach(([cx, cy], b) => {
      for (let i = 0; i < 26; i++) {
        const angle = (i / 26) * Math.PI * 2;
        const dist = 70 + Math.random() * 70;
        out.push({ cx, cy, tx: Math.cos(angle) * dist, ty: Math.sin(angle) * dist, color: COLORS[(i + b) % COLORS.length], delay: b * 0.35 + Math.random() * 0.2 });
      }
    });
    return out;
  }, [final]);

  const confetti = useMemo(() => Array.from({ length: final ? 70 : 48 }, (_, i) => ({
    left: Math.random() * 100, color: COLORS[i % COLORS.length], delay: Math.random() * 1.4, dur: 2.2 + Math.random() * 1.5, w: 6 + Math.random() * 6,
  })), [final]);

  const title = final
    ? txt('Toutes les réserves sont faites !', 'Все замечания выполнены!', 'Toate rezervele sunt făcute!', 'Усі зауваження виконано!')
    : txt(`Bravo${name ? ` ${name}` : ''} !`, `Молодец${name ? `, ${name}` : ''}!`, `Bravo${name ? ` ${name}` : ''}!`, `Молодець${name ? `, ${name}` : ''}!`);
  const sub = final
    ? txt('Félicitations à toute l\'équipe, beau travail. Pavel passe tout en revue.', 'Поздравляем всю команду, отличная работа! Павел всё проверит.', 'Felicitări întregii echipe, treabă bună! Pavel verifică totul.', 'Вітаємо всю команду, чудова робота! Павел усе перевірить.')
    : txt(`Réserve n° ${number} terminée. Merci pour le travail bien fait !`, `Замечание №${number} выполнено. Спасибо за хорошую работу!`, `Rezerva nr. ${number} terminată. Mulțumim pentru lucrul bine făcut!`, `Зауваження №${number} виконано. Дякуємо за добру роботу!`);

  return (
    <>
      <style>{styles}</style>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/55 backdrop-blur-[2px]" onClick={onClose}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confetti.map((c, i) => (
            <div key={`c${i}`} className="absolute rounded-sm" style={{ left: `${c.left}%`, top: '-20px', width: c.w, height: c.w * 1.6, backgroundColor: c.color, animation: `rc-fall ${c.dur}s ease-in ${c.delay}s forwards` }} />
          ))}
          {bursts.map((p, i) => (
            <div key={`p${i}`} className="absolute w-2.5 h-2.5 rounded-full" style={{ left: `${p.cx}%`, top: `${p.cy}%`, backgroundColor: p.color, '--tx': `${p.tx}px`, '--ty': `${p.ty}px`, animation: `rc-burst 1.3s ease-out ${p.delay}s forwards` }} />
          ))}
        </div>
        <div className="relative bg-white rounded-3xl shadow-2xl px-7 py-8 max-w-sm mx-4 text-center" style={{ animation: 'rc-pop 0.5s ease-out forwards' }} onClick={(e) => e.stopPropagation()}>
          <div className="text-6xl mb-3" style={{ animation: 'rc-shine 1.2s ease-in-out infinite' }}>{final ? '🏆' : '🎉'}</div>
          <h2 className="text-2xl font-bold text-teal-700 mb-2">{title}</h2>
          <p className="text-gray-600">{sub}</p>
        </div>
      </div>
    </>
  );
}
